import { selectIsSystemSession } from 'features/application/selectors';
import { store } from 'store/store';
import { emitTelemetryEvent, TelemetryEvent } from 'utils/sentry/telemetryEvent';
import { HangupReason } from 'utils/webrtc/hangupReason';
import type { IceServerListProblem } from 'utils/webrtc/turnCredentials';

export type IceFeedType = 'receiving' | 'publishing' | 'screensharing';
export type StallEntry = 'detector' | 'dtls_failed' | 'ice_disconnected' | 'janus_hangup';
export type RebuildOutcome =
  'connected' | 'connect_timeout' | 'budget_exhausted' | 'threw' | 'no_op' | 'token_refresh_failed';
export type HangupRouted = 'rebuild' | 'modal' | 'ignored';

export interface DtlsStallAttributes {
  feedType: IceFeedType;
  entry: StallEntry;
  iceConnectMs: number | null;
  connectedLifetimeMs: number | null;
  relayRequiredBefore: boolean;
  recoveryEnabled: boolean;
  // Lets a Sentry query dedupe repeat entries against one dying PC without a PC id.
  firstForPc: boolean;
}

export interface DtlsRebuildAttributes {
  feedType: IceFeedType;
  outcome: RebuildOutcome;
  attempt: number;
  rebuildMs: number | null;
  policy: 'relay';
  error: string | null;
}

export interface JanusHangupAttributes {
  feedType: IceFeedType;
  reason: HangupReason;
  rawReason: string | null;
  dtlsEverConnected: boolean;
  routed: HangupRouted;
}

const ICE_DTLS_STALL_MESSAGE = '[ICE] DTLS stall';
const ICE_DTLS_REBUILD_MESSAGE = '[ICE] DTLS rebuild';
const ICE_JANUS_HANGUP_MESSAGE = '[ICE] Janus hangup';

const emitIceEvent = (
  input: Pick<TelemetryEvent, 'event' | 'message' | 'level' | 'cap'> & {
    attributes: object;
  }
) =>
  emitTelemetryEvent({
    ...input,
    channel: 'streaming',
    attributes: {
      ...input.attributes,
      isSystemSession: selectIsSystemSession(store.getState()),
      msSincePageLoad: Math.round(performance.now()),
    },
  });

export const emitDtlsStall = (attributes: DtlsStallAttributes) =>
  emitIceEvent({
    event: 'ice.dtls.stall',
    message: `${ICE_DTLS_STALL_MESSAGE} — ${attributes.entry}`,
    level: 'warn',
    cap: 10,
    attributes,
  });

export const emitDtlsRebuild = (attributes: DtlsRebuildAttributes) =>
  emitIceEvent({
    event: 'ice.dtls.rebuild',
    message: `${ICE_DTLS_REBUILD_MESSAGE} — ${attributes.outcome}`,
    level: attributes.outcome === 'connected' ? 'info' : 'warn',
    cap: 20,
    attributes,
  });

export const emitJanusHangup = (attributes: JanusHangupAttributes) =>
  emitIceEvent({
    event: 'ice.janus.hangup',
    message: `${ICE_JANUS_HANGUP_MESSAGE} — ${attributes.reason}`,
    level: 'warn',
    cap: 10,
    attributes,
  });

export interface TurnRenewalRejectedAttributes {
  problem: IceServerListProblem;
}

const ICE_TURN_RENEWAL_REJECTED_MESSAGE = '[ICE] TURN renewal rejected';

export const emitTurnRenewalRejected = (attributes: TurnRenewalRejectedAttributes) =>
  emitIceEvent({
    event: 'ice.turn.renewal.rejected',
    message: `${ICE_TURN_RENEWAL_REJECTED_MESSAGE} — ${attributes.problem}`,
    level: 'warn',
    cap: 5,
    attributes,
  });

export interface TurnUnauthorizedAttributes {
  feedType: IceFeedType;
  errorCount: number;
  url: string | null;
  credentialRemainingSec: number | null;
  relayOnly: boolean;
}

const ICE_TURN_UNAUTHORIZED_MESSAGE = '[ICE] TURN unauthorized';

export const emitTurnUnauthorized = (attributes: TurnUnauthorizedAttributes) =>
  emitIceEvent({
    // Sentry scrubs an `event` value containing "auth" to [Filtered], which breaks `event:` queries.
    event: 'ice.turn.refused',
    message: ICE_TURN_UNAUTHORIZED_MESSAGE,
    level: 'warn',
    cap: 20,
    attributes,
  });
